/**
 * **Rute dapur — PER OUTLET.**
 *
 * ### ⭐⭐⭐ Kenapa layar ini berdiri sendiri, bukan satu tab di dalam produk
 * Kasus nyatanya (Imron, Penang Bistro): "Roti Canai" keluar di **KDS ASIA** (PB-KBS), **printer
 * PRT_DESSERT** (PB-PKB), dan **KDS WESTERN** (PB-MPP). Yang menentukan tujuan bukan jenis
 * makanannya melainkan **jarak meja penyiapan ke stasiun** di outlet itu.
 *
 * ⇒ Karena itu layar ini dimulai dari **OUTLET**, baru produk. Formulir produk yang punya kolom
 *   "kitchen printer" (ala VTEC) memaksa ketiga outlet bertujuan sama — dan yang terjadi
 *   berikutnya adalah tiket tercetak di ruangan sebelah, lalu makanan yang tak pernah dimasak.
 *
 * ⛔ Outlet tanpa stasiun ditandai keras: tanpa rute DAN tanpa stasiun cadangan, `send` menolak
 *   (`ORDER-409-NO_ROUTE`). Itu disengaja — tiket yang hilang tak pernah menghasilkan galat.
 *
 * ### ⚠️ Hanya SISI STASIUN yang jadi `CrudPanel` (Imron, 2026-08-03)
 * Stasiun punya isi yang bisa disunting: nama, jenis, cadangan, urutan. **Rute tidak** — ia baris
 * penghubung produk↔stasiun tanpa satu pun kolom yang boleh berubah (`update: z.object({})` di
 * registry). Laci sunting untuk rute akan kosong, dan memindah rute memang **hapus lalu buat**:
 * menyuntingnya di tempat akan menghapus jejak "dulu ke mana" — pertanyaan pertama saat tiket
 * tercetak di ruangan yang salah.
 */
import { DeleteOutlined } from '@ant-design/icons';
import {
  Alert, Button, Card, Col, Empty, Form, Input, InputNumber, Row, Select, Space, Switch, Table, Tag,
  Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PRINT_STATION_KINDS } from '@osbpos/contract';

import { api } from '../api/client';
import type {
  Category, Department, Outlet, PrintStation, Product, ProductRoute, ReadinessCheck,
} from '../api/client';
import { canWrite } from '../api/session';
import { takeOutletContext } from '../layout/navigate';
import { CrudPanel } from '../components/CrudPanel';

/**
 * ⚠️ Diambil dari KONTRAK, bukan disalin: daftar yang disalin akan menua diam-diam, dan jenis
 * stasiun baru tak pernah muncul di layar tanpa ada yang menyadari kenapa.
 */
const KINDS = PRINT_STATION_KINDS;

/** Baris tabel rute — sudah diratakan supaya kolomnya tak perlu mencari stasiun lagi. */
interface BarisRute {
  readonly id: string;
  readonly stationCode: string;
  readonly stationKind: string;
}

export function Routing() {
  const { t } = useTranslation();
  const boleh = canWrite();

  const [outlet, setOutlet] = useState<readonly Outlet[]>([]);
  const [outletId, setOutletId] = useState<string | null>(null);
  /*
   * ⭐ Outlet awal boleh datang dari tautan pintas kesiapan (`navigate.ts`) — kalau tidak, layar ini
   * terbuka kosong dan orang yang baru saja mengklik "perbaiki di outlet X" harus memilihnya lagi
   * sambil menebak outlet mana tadi yang dimaksud.
   *
   * ⚠️ Diambil di EFEK, bukan di penginisialisasi `useState`. `StrictMode` menjalankan
   * penginisialisasi DUA KALI: panggilan pertama menghabiskan konteksnya, panggilan kedua dapat
   * `undefined` — dan yang kedua itulah yang dipakai. Gejalanya persis seperti fitur yang tak
   * pernah terpasang.
   */
  useEffect(() => {
    const dariTautan = takeOutletContext();
    if (dariTautan !== undefined) setOutletId(dariTautan);
  }, []);
  const [station, setStation] = useState<readonly PrintStation[]>([]);
  const [tick, setTick] = useState(0);

  const [department, setDepartment] = useState<readonly Department[]>([]);
  const [category, setCategory] = useState<readonly Category[]>([]);
  const [product, setProduct] = useState<readonly Product[]>([]);
  const [depId, setDepId] = useState<string | null>(null);
  const [katId, setKatId] = useState<string | null>(null);
  const [prodId, setProdId] = useState<string | null>(null);
  const [route, setRoute] = useState<readonly ProductRoute[]>([]);

  useEffect(() => { void api.outlets().then(setOutlet).catch(() => undefined); }, []);

  /**
   * ⭐⭐⭐ **Berapa produk di outlet ini yang belum punya TUJUAN** — diambil dari gerbang kesiapan,
   * bukan dihitung ulang di sini.
   *
   * ⛔ Kalau layar punya rumusnya sendiri, ia akan menyimpang dari gerbang yang sesungguhnya
   * menolak (`openShift` → `checkOutletReadiness`). Dan yang menyimpang terbaca sebagai "layar
   * bilang beres, kasir tetap ditolak" — pesan paling membingungkan yang bisa dihasilkan sistem ini.
   *
   * ⚠️ Sengaja dimuat ulang tiap kali rute berubah (`tick`): angka yang tak ikut turun saat orang
   * baru saja memperbaikinya membuat orang mengira perbaikannya tak tersimpan.
   */
  const [rute, setRute] = useState<ReadinessCheck | null>(null);
  const kodeOutlet = outlet.find((o) => o.id === outletId)?.code ?? null;
  useEffect(() => {
    if (kodeOutlet === null) { setRute(null); return; }
    void api
      .readiness(kodeOutlet)
      .then((r) => { setRute(r.checks.find((c) => c.key === 'product_route') ?? null); })
      /* ⚠️ Gagal memeriksa TIDAK boleh menutup layarnya — rute tetap bisa disunting tanpa angka. */
      .catch(() => { setRute(null); });
  }, [kodeOutlet, tick, prodId]);
  /**
   * ⭐⭐ **Departemen disaring menurut BRAND outlet terpilih** (Imron, 2026-08-06: *"rute dapur
   * masih menampilkan department/kategori milik brand lain"*).
   *
   * ⛔ `department.brand_id` NOT NULL sejak awal, dan komentarnya sudah menyebut alasannya:
   * *"struktur menu Penang Bistro tak ada urusannya dengan Seribu Rasa; menyatukannya membuat
   * daftar departemen satu brand penuh baris milik brand lain — dan salah pilih tak bergejala
   * sampai laporan per-departemen dibaca."* Layar ini memanggilnya **tanpa saringan**.
   *
   * ⇒ Akibatnya di sini lebih jauh dari sekadar daftar yang ramai: rute yang dibuat menempelkan
   *   produk brand LAIN ke stasiun cetak outlet ini — tiket yang tak akan pernah dikirim, atau
   *   lebih buruk, dikirim untuk produk yang tak dijual di sini.
   *
   * ⚠️ Tanpa outlet terpilih, daftarnya KOSONG — bukan "semua". Rute milik outlet; menampilkan
   * seluruh departemen tenant sebelum outletnya dipilih cuma mengundang salah pilih.
   */
  const brandOutlet = outlet.find((o) => o.id === outletId)?.brand_id;
  useEffect(() => {
    if (brandOutlet === undefined) { setDepartment([]); return; }
    void api.departments(brandOutlet).then(setDepartment).catch(() => undefined);
  }, [brandOutlet]);

  /* ⛔ `includeInactive`: layar pengelola — stasiun yang dimatikan harus tetap bisa dinyalakan. */
  const muatStation = useCallback(async (): Promise<readonly PrintStation[]> => {
    if (outletId === null) { setStation([]); return []; }
    const s = await api.stations(outletId, true);
    setStation(s);
    return s;
  }, [outletId]);
  useEffect(() => { void muatStation().catch(() => undefined); }, [muatStation]);

  useEffect(() => {
    if (depId === null) { setCategory([]); return; }
    void api.categories(depId).then(setCategory).catch(() => undefined);
  }, [depId]);
  useEffect(() => {
    if (katId === null) { setProduct([]); return; }
    void api.products(katId).then(setProduct).catch(() => undefined);
  }, [katId]);

  const muatRoute = useCallback(() => {
    if (prodId === null) { setRoute([]); return; }
    void api.routes(prodId).then(setRoute).catch(() => undefined);
  }, [prodId]);
  useEffect(() => { muatRoute(); }, [muatRoute]);

  /*
   * ⚠️ Rute disaring ke stasiun MILIK OUTLET TERPILIH. Produk yang sama punya rute di outlet lain
   * juga — menampilkan semuanya bercampur membuat "sudah dirutekan?" mustahil dijawab sekilas.
   */
  const stationId = new Set(station.map((s) => s.id));
  const routeDiOutlet = route.filter((r) => stationId.has(r.print_station_id));
  const belumDirute = prodId !== null && outletId !== null && routeDiOutlet.length === 0;

  const kolomStation: ColumnsType<PrintStation> = [
    {
      title: t('routing.code'),
      dataIndex: 'code',
      width: 150,
      render: (v: string, r: PrintStation) => (
        <span data-testid={`stasiun-${v}`}>
          <b>{v}</b> {r.is_default && <Tag color="orange">{t('routing.isDefault')}</Tag>}
        </span>
      ),
    },
    { title: t('routing.name'), dataIndex: 'name' },
    {
      title: t('routing.kind'),
      dataIndex: 'kind',
      width: 170,
      /* ⭐ `counter` ditandai beda: ia tujuan yang SAH tapi tak menerbitkan tiket — dan orang yang
         membaca daftar ini harus tahu bedanya tanpa membuka dokumen. */
      render: (v: string) =>
        v === 'counter'
          ? <Tag color="gold" data-testid="jenis-counter">{t('routing.counter')}</Tag>
          : <Tag>{v}</Tag>,
    },
    {
      title: '',
      dataIndex: 'is_active',
      width: 90,
      render: (v: boolean) =>
        v ? <Tag color="green">{t('outlets.active')}</Tag> : <Tag>{t('outlets.inactive')}</Tag>,
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="routing">
      <Alert type="info" showIcon title={t('routing.perOutlet')} data-testid="routing-catatan" />

      {/*
        ⭐⭐⭐ **Ringkasan seluruh outlet, DI ATAS** — sebelum orang menelusuri produk satu per satu.
        Tanpa ini, "berapa yang belum dirutekan?" hanya bisa dijawab dengan membuka tiap produk,
        dan pertanyaan yang mahal dijawab adalah pertanyaan yang berhenti ditanyakan.

        ⚠️ Isinya datang dari GERBANG KESIAPAN, tak dihitung ulang di layar — supaya angka di sini
        dan alasan `openShift` menolak tak pernah bisa berselisih.

        ⛔ Dua bentuk, sengaja: `warning` bila yang terlupa **tak akan sampai dapur** (bawaan
        `counter`/tak ada bawaan), `info` bila masih sampai (bawaan bertiket). Menyamakan keduanya
        membuat yang berbahaya terbaca sama dengan yang cuma berantakan.

        ⚠️ Sejak 2026-08-23 tak satu pun dari keduanya **menghentikan penjualan** (Imron: *"ada
        atau tidaknya printer/KDS di kitchen tidak boleh menghentikan penjualan"*). Kalimatnya
        ikut diperbaiki: peringatan yang mengancam sesuatu yang tak lagi terjadi akan berhenti
        dibaca, lalu yang sungguhan pun ikut diabaikan.
      */}
      {rute !== null && (
        <Alert
          type={rute.ok ? 'info' : 'warning'}
          showIcon
          data-testid={rute.ok ? 'rute-catatan' : 'rute-blocker'}
          title={rute.ok ? t('routing.unroutedInfo') : t('routing.unroutedBlocker')}
          description={rute.detail}
        />
      )}

      <Row gutter={16}>
        <Col span={10}>
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            <span data-testid="pilih-outlet">
              <Select
                style={{ width: 260 }}
                placeholder={t('routing.outlet')}
                value={outletId}
                onChange={setOutletId}
                options={outlet.map((o) => ({ value: o.id, label: `${o.code} · ${o.name}` }))}
              />
            </span>

            {outletId === null ? (
              <Card size="small" title={t('routing.stations')}>
                <Empty description={t('routing.pickOutlet')} data-testid="stasiun-pilih-outlet" />
              </Card>
            ) : (
              <>
                {station.length === 0 && (
                  /* ⛔ Bukan "belum ada data": outlet ini TAK BISA mengirim pesanan sama sekali. */
                  <Alert
                    type="error"
                    showIcon
                    title={t('routing.noStation')}
                    data-testid="stasiun-kosong"
                  />
                )}
                <CrudPanel<PrintStation>
                  testId="stasiun"
                  title={t('routing.stations')}
                  subtitle={t('routing.stationSubtitle')}
                  canWrite={boleh}
                  newLabel={t('routing.addStation')}
                  columns={kolomStation}
                  fetchList={muatStation}
                  reloadToken={tick}
                  searchIn={(s) => `${s.code} ${s.name}`}
                  renderFields={(_f, editing) => (
                    <>
                      {editing === null ? (
                        <Form.Item
                          name="code"
                          label={t('routing.code')}
                          rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                          tooltip={t('structure.codeFixed')}
                        >
                          <Input maxLength={40} data-testid="stasiun-kode" />
                        </Form.Item>
                      ) : (
                        <Alert type="info" showIcon style={{ marginBottom: 16 }}
                               title={t('structure.codeLocked')} />
                      )}
                      <Form.Item name="name" label={t('routing.name')} rules={[{ required: true, min: 2 }]}>
                        <Input maxLength={120} data-testid="stasiun-nama" />
                      </Form.Item>
                      {/* ⚠️ Jenis BOLEH berubah: printer kertas kerap diganti KDS layar, dan itu
                          penggantian perangkat — bukan stasiun baru dengan sejarah yang terputus. */}
                      <Form.Item name="kind" label={t('routing.kind')} initialValue="prep_printer"
                                 tooltip={t('routing.counterHint')}>
                        <Select
                          options={KINDS.map((k) => ({
                            value: k,
                            label: k === 'counter' ? `${k} — ${t('routing.counter')}` : k,
                          }))}
                        />
                      </Form.Item>
                      <Form.Item name="sortOrder" label={t('structure.sortOrder')} initialValue={0}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                      {/* ⭐⭐ Stasiun cadangan: tujuan produk yang belum dirutekan. Tanpa satu pun
                          cadangan, `send` MENOLAK — dan itu disengaja (`ORDER-409-NO_ROUTE`). */}
                      <Form.Item name="isDefault" label={t('routing.isDefault')} valuePropName="checked">
                        {/* ⚠️ Diberi kata juga: tuas tanpa tulisan memaksa orang menebak arti
                            posisinya, dan yang ditebak di sini menentukan pesanan bisa dikirim
                            atau ditolak. */}
                        <Switch
                          data-testid="stasiun-cadangan"
                          checkedChildren={t('common.on')}
                          unCheckedChildren={t('common.off')}
                        />
                      </Form.Item>
                      <Alert type="info" showIcon title={t('routing.defaultHint')} />
                      {editing !== null && (
                        <Form.Item name="isActive" label={t('outlets.status')} valuePropName="checked"
                                   style={{ marginTop: 16 }}>
                          <Switch
                            data-testid="stasiun-aktif"
                            checkedChildren={t('outlets.active')}
                            unCheckedChildren={t('outlets.inactive')}
                          />
                        </Form.Item>
                      )}
                    </>
                  )}
                  toFormValues={(s) => ({
                    name: s.name, kind: s.kind, sortOrder: s.sort_order,
                    isDefault: s.is_default, isActive: s.is_active,
                  })}
                  onCreate={async (v) => {
                    if (outletId === null) return;
                    await api.createStation({
                      outletId,
                      code: String(v['code']).toUpperCase(),
                      name: String(v['name']),
                      kind: String(v['kind']),
                      isDefault: v['isDefault'] === true,
                      sortOrder: Number(v['sortOrder'] ?? 0),
                    });
                    setTick((n) => n + 1);
                  }}
                  onUpdate={async (id, v, lama) => {
                    await api.updateMaster('print-stations', id, {
                      name: v['name'],
                      kind: v['kind'],
                      sortOrder: Number(v['sortOrder'] ?? 0),
                      isDefault: v['isDefault'] === true,
                    });
                    if (v['isActive'] !== lama.is_active) {
                      await api.setStatus('print-stations', id, v['isActive'] === true);
                    }
                    setTick((n) => n + 1);
                  }}
                />
              </>
            )}
          </Space>
        </Col>

        <Col span={14}>
          <Card size="small" title={t('routing.routes')}>
            {/* Produk dipilih berurut: departemen → kategori → produk (hierarki yang sama). */}
            <Space wrap style={{ marginBottom: 12 }}>
              <span data-testid="pilih-dep">
                <Select
                  style={{ width: 180 }}
                  placeholder={t('master.departments')}
                  value={depId}
                  onChange={(v: string) => { setDepId(v); setKatId(null); setProdId(null); }}
                  options={department.map((d) => ({ value: d.id, label: `${d.code} · ${d.name}` }))}
                />
              </span>
              <span data-testid="pilih-kat">
                <Select
                  style={{ width: 180 }}
                  placeholder={t('master.categories')}
                  value={katId}
                  disabled={depId === null}
                  onChange={(v: string) => { setKatId(v); setProdId(null); }}
                  options={category.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))}
                />
              </span>
              <span data-testid="pilih-produk">
                <Select
                  style={{ width: 220 }}
                  placeholder={t('routing.product')}
                  value={prodId}
                  disabled={katId === null}
                  onChange={setProdId}
                  options={product.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))}
                />
              </span>
            </Space>

            {prodId === null || outletId === null ? (
              <Empty
                description={outletId === null ? t('routing.pickOutlet') : t('routing.pickProduct')}
                data-testid="rute-pilih-dulu"
              />
            ) : (
              <>
                {belumDirute && (
                  <Alert
                    type="warning"
                    showIcon
                    title={t('routing.noRoute')}
                    data-testid="rute-kosong"
                    style={{ marginBottom: 12 }}
                  />
                )}
                <Table<BarisRute>
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={routeDiOutlet.map((r) => ({
                    ...r,
                    stationCode: station.find((x) => x.id === r.print_station_id)?.code ?? '?',
                    stationKind: station.find((x) => x.id === r.print_station_id)?.kind ?? '',
                  }))}
                  columns={[
                    {
                      title: t('routing.station'),
                      key: 'station',
                      render: (_: unknown, r: BarisRute) => (
                        <span data-testid={`rute-${r.stationCode}`}>
                          {r.stationCode} <Tag>{r.stationKind}</Tag>
                        </span>
                      ),
                    },
                    ...(boleh
                      ? [
                          {
                            title: '',
                            key: 'aksi',
                            width: 120,
                            render: (_: unknown, r: BarisRute) => (
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                data-testid={`rute-lepas-${r.id}`}
                                onClick={() => {
                                  void api.deleteRoute(r.id).then(muatRoute).catch(() => undefined);
                                }}
                              >
                                {t('routing.remove')}
                              </Button>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />

                {boleh && station.length > 0 && (
                  <Space style={{ marginTop: 12 }}>
                    <span data-testid="rute-tambah-stasiun">
                      <Select
                        style={{ width: 240 }}
                        placeholder={t('routing.station')}
                        options={station
                          .filter((s) => !stationId.has(s.id) || !routeDiOutlet.some((r) => r.print_station_id === s.id))
                          .map((s) => ({ value: s.id, label: `${s.code} · ${s.kind}` }))}
                        onChange={(v: string) => {
                          void api
                            .createRoute({ productId: prodId, printStationId: v })
                            .then(muatRoute)
                            .catch((e: Error) => { void message.error(e.message); });
                        }}
                      />
                    </span>
                    <Typography.Text type="secondary">{t('routing.routes')}</Typography.Text>
                  </Space>
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
